"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ExternalLink,
  Eye,
  Heart,
  MessageSquare,
  Newspaper,
  RefreshCw,
  TrendingUp,
  TriangleAlert,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { HumStatsResponse } from "@/types/mission";
import { timeAgo } from "@/components/mission/sections";

const POLL_SECONDS = 10 * 60; // server caches upstream for 10 min — match it

/**
 * Publishing-lane traction (OPP-043): live hum.pub platform totals plus
 * per-article view counters for the mission's author, fetched through
 * /api/mission/hum (public endpoints only — no secrets involved).
 *
 * Honesty rules mirrored from the rest of the dashboard: view counters are
 * platform-reported traction, NEVER earnings; the card says so in plain text.
 */
export function HumTractionCard() {
  const [data, setData] = useState<HumStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const mounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/mission/hum", { cache: "no-store" });
      const json = (await res.json()) as HumStatsResponse;
      if (mounted.current) setData(json);
    } catch {
      if (mounted.current) setData({ ok: false, error: "network error reaching the hum traction route" });
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    const poll = window.setInterval(() => {
      if (!document.hidden) load();
    }, POLL_SECONDS * 1000);
    const clock = window.setInterval(() => {
      if (!document.hidden) setNow(Date.now());
    }, 30_000);
    return () => {
      mounted.current = false;
      window.clearInterval(poll);
      window.clearInterval(clock);
    };
  }, [load]);

  const fresh = data && data.ok ? data : null;
  const maxViews = fresh?.author
    ? Math.max(1, ...fresh.author.articles.map((a) => a.views))
    : 1;
  /* `now` in the deps is deliberate — it re-derives the relative label on each clock tick */
  const fetchedLabel = useMemo(() => (fresh ? timeAgo(fresh.fetched_at) : null), [fresh, now]);

  return (
    <Card className="border-zinc-800/70 bg-zinc-900/40 shadow-none">
      <CardHeader className="pb-3">
        <CardTitle className="flex min-w-0 flex-wrap items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">
          <Newspaper className="size-4 shrink-0 text-emerald-400" aria-hidden="true" />
          <span className="truncate">Publishing Traction — hum.pub</span>
          <Badge
            variant="outline"
            className="border-emerald-500/30 bg-emerald-500/10 font-mono text-[9px] tracking-wider text-emerald-400"
          >
            OPP-043
          </Badge>
          {fresh?.stale && (
            <Badge
              variant="outline"
              className="border-amber-500/40 bg-amber-500/10 font-mono text-[9px] tracking-wider text-amber-400"
              title="hum.pub was unreachable on the last refresh — showing the most recent successful fetch"
            >
              stale data
            </Badge>
          )}
          <span className="ml-auto flex items-center gap-2">
            {fetchedLabel && (
              <span className="font-mono text-[10px] font-normal normal-case tracking-normal text-zinc-600">
                fetched {fetchedLabel}
              </span>
            )}
            <Button
              variant="outline"
              size="icon"
              className="size-7 border-zinc-800 bg-zinc-900/60 text-zinc-500 hover:text-zinc-100"
              onClick={load}
              disabled={loading}
              aria-label="Refresh hum.pub traction"
              title="Refresh platform counters"
            >
              <RefreshCw className={`size-3 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
            </Button>
          </span>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* loading skeleton */}
        {loading && !fresh && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14 rounded-md bg-zinc-900/60" />
              ))}
            </div>
            <Skeleton className="h-20 w-full rounded-md bg-zinc-900/60" />
          </div>
        )}

        {/* honest unavailable state — never fake traction */}
        {data && !data.ok && !loading && (
          <div
            role="note"
            className="flex items-center gap-3 rounded-md border border-zinc-800/70 bg-zinc-900/30 px-3 py-3 text-xs text-zinc-500"
          >
            <TriangleAlert className="size-4 shrink-0 text-amber-500/70" aria-hidden="true" />
            <p className="min-w-0 break-words">
              hum.pub traction unavailable right now —{" "}
              <span className="font-mono text-[10px]">{data.error}</span>. The publishing lane itself
              is tracked in the Pipeline (OPP-043); only these live counters are offline.
            </p>
          </div>
        )}

        {fresh && (
          <>
            {/* platform totals */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: "platform authors", value: fresh.platform.authors.toLocaleString() },
                { label: "platform articles", value: fresh.platform.articles.toLocaleString() },
                { label: "platform views", value: fresh.platform.views.toLocaleString() },
              ].map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.3 }}
                  className="rounded-md border border-zinc-800/70 bg-zinc-900/30 px-3 py-2"
                >
                  <p className="font-mono text-lg font-bold tabular-nums leading-tight text-zinc-100">
                    {s.value}
                  </p>
                  <p className="mt-0.5 font-mono text-[9px] uppercase tracking-wider text-zinc-600">
                    {s.label}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* our articles */}
            {fresh.author ? (
              <div className="space-y-2">
                <p className="text-[11px] leading-relaxed text-zinc-500">
                  Author <span className="font-mono text-emerald-300">{fresh.author.name}</span> —{" "}
                  {fresh.author.articles.length} published{" "}
                  {fresh.author.articles.length === 1 ? "article" : "articles"},{" "}
                  {fresh.author.total_views.toLocaleString()} platform-reported{" "}
                  {fresh.author.total_views === 1 ? "view" : "views"}
                  {(fresh.author.total_views_delta ?? 0) > 0 && fresh.delta_since && (
                    <span
                      className="ml-1 inline-flex items-center gap-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 px-1 font-mono text-[10px] font-semibold tabular-nums text-emerald-400"
                      title={`views gained since ${new Date(fresh.delta_since).toISOString().slice(0, 16).replace("T", " ")}Z (server snapshot diff)`}
                    >
                      <TrendingUp className="size-2.5" aria-hidden="true" />
                      +{fresh.author.total_views_delta}
                    </span>
                  )}
                  .
                </p>
                <ul className="space-y-1.5">
                  {fresh.author.articles.map((a, i) => (
                    <motion.li
                      key={a.id}
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.06 * i, duration: 0.3 }}
                      className="group rounded-md border border-zinc-800/60 bg-zinc-900/20 px-3 py-2 transition-colors hover:border-emerald-500/25 hover:bg-zinc-900/40 motion-reduce:transition-none"
                    >
                      <div className="flex min-w-0 items-start justify-between gap-2">
                        <a
                          href={`https://hum.pub/analysis/${a.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="min-w-0 break-words text-xs font-medium leading-snug text-zinc-300 underline decoration-zinc-700 underline-offset-2 transition-colors hover:text-emerald-300 hover:decoration-emerald-500/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60"
                        >
                          {a.title}
                          <ExternalLink
                            className="ml-1 inline size-3 shrink-0 text-zinc-600 transition-colors group-hover:text-emerald-400"
                            aria-hidden="true"
                          />
                        </a>
                        <span className="flex shrink-0 items-center gap-1 font-mono text-[10px] tabular-nums text-zinc-400">
                          <Eye className="size-3 text-zinc-600" aria-hidden="true" />
                          {a.views}
                          {(a.views_delta ?? 0) > 0 && fresh.delta_since && (
                            <span
                              className="inline-flex items-center rounded border border-emerald-500/30 bg-emerald-500/10 px-1 font-semibold text-emerald-400"
                              title={`+${a.views_delta} views since ${new Date(fresh.delta_since).toISOString().slice(0, 16).replace("T", " ")}Z`}
                            >
                              +{a.views_delta}
                            </span>
                          )}
                        </span>
                      </div>
                      {/* view-count bar — scaled to our own best performer */}
                      <div className="mt-1.5 flex items-center gap-2">
                        <div
                          className="h-1 rounded-full bg-gradient-to-r from-emerald-500/70 to-teal-400/50 transition-[width] duration-500 motion-reduce:transition-none"
                          style={{ width: `${Math.max(4, (a.views / maxViews) * 100)}%` }}
                          aria-hidden="true"
                        />
                        <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-600">
                          {a.category} ·{" "}
                          {new Date(a.published_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                          {a.reactions > 0 && (
                            <span className="ml-1 inline-flex items-center gap-0.5">
                              <Heart className="inline size-2.5 text-rose-400/70" aria-hidden="true" />
                              {a.reactions}
                              {(a.reactions_delta ?? 0) > 0 && (
                                <span className="font-semibold text-rose-300">+{a.reactions_delta}</span>
                              )}
                            </span>
                          )}
                          {a.comments > 0 && (
                            <span className="ml-1 inline-flex items-center gap-0.5">
                              <MessageSquare className="inline size-2.5 text-zinc-500" aria-hidden="true" />
                              {a.comments}
                              {(a.comments_delta ?? 0) > 0 && (
                                <span className="font-semibold text-zinc-300">+{a.comments_delta}</span>
                              )}
                            </span>
                          )}
                        </span>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-[11px] text-zinc-600">
                No tracked articles found in the public listing — new publishes appear here within
                one refresh cycle.
              </p>
            )}

            {/* honesty footnote */}
            <p className="border-t border-zinc-800/50 pt-2 text-[10px] leading-relaxed text-zinc-600">
              Counters are platform-reported traction signals, refreshed live from hum.pub&apos;s
              public API — they are <span className="text-zinc-400">not earnings</span>. Verified
              earnings appear exclusively in the Earnings tab, gated on on-chain or platform payout
              evidence. Updated {fetchedLabel} · auto-refresh every 10 min
              {fresh.delta_since
                ? ` · deltas vs snapshot ${new Date(fresh.delta_since).toISOString().slice(0, 16).replace("T", " ")}Z`
                : " · deltas appear once a second server snapshot exists"}
              .
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
